<script setup>
import { computed } from 'vue'
import AppBadge from '@/components/atoms/AppBadge'
import AppButton from '@/components/atoms/AppButton'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import AppCard from '@/components/molecules/AppCard'
import AppCarousel from '@/components/molecules/AppCarousel'
import { useStrapiStore } from '@/stores/StrapiStore'
import { safeCapture } from '@/helpers/posthog'
import { POSTHOG_EVENTS } from '@/constants'

// The newest releases the row shows; the changelog page has the rest.
const POST_COUNT = 5

const strapiStore = useStrapiStore()

// A card per release: its first section carries the headline and the summary, the post's own title is the version.
const posts = computed(() => strapiStore.changeLog.slice(0, POST_COUNT).map((post) => {
  const [section] = post.attributes.section ?? []
  return {
    id: post.id,
    title: section?.title ?? post.attributes.title,
    description: section?.description ?? '',
  }
}))

// The store keeps the posts across visits; the changelog page reads the same list.
if (!strapiStore.changeLog.length) strapiStore.fetchChangeLogData()

const trackPost = (post) => safeCapture(POSTHOG_EVENTS.WHATS_NEW_CLICKED, { item_id: post.id })
</script>

<template>
  <!-- The row's shape while the posts load: its title row, then the cards as the design's blocks at our width (Figma 29796:69280). -->
  <section v-if="strapiStore.changeLogLoading && !posts.length" role="status" class="flex flex-col gap-y-4 border-t border-blue-50 px-4 py-5">
    <span class="sr-only">Loading What's new</span>

    <div class="flex items-center justify-between gap-x-2">
      <AppSkeleton width="w-24" height="h-5" />
      <AppSkeleton width="w-24" height="h-4" />
    </div>

    <!-- Bleeds like the carousel, so the second card peeks in. -->
    <div class="-mx-4 flex gap-x-4 overflow-hidden px-4">
      <AppSkeleton v-for="card in 2" :key="card" class="shrink-0" width="w-72" height="h-48" rounded="rounded-xl" />
    </div>
  </section>

  <!-- Nothing until the CMS has posts, so the row never sits empty. -->
  <section v-else-if="posts.length" class="flex flex-col gap-y-4 border-t border-blue-50 px-4 py-5">
    <header class="flex items-center justify-between gap-x-2">
      <h3 class="text-base font-semibold text-gray-900">What's new</h3>

      <AppButton as="router-link" to="/changelog" variant="link" size="sm" class="-mr-2">
        View changelog
      </AppButton>
    </header>

    <!-- Bleeds to the panel's edges so the next card peeks in. -->
    <AppCarousel arrows="overlay" :bleed="16" gap="gap-x-4" aria-label="What's new">
      <AppCard v-for="(post, index) in posts" :key="post.id" as="article" variant="soft-gradient" size="sm" class="w-72">
        <template #title>
          <!-- Only the newest release is new; the badge sits beside the title so the cards keep one height. -->
          <div class="flex min-w-0 flex-1 items-start gap-x-3">
            <AppBadge v-if="index === 0" size="xs" custom-class="bg-tint-1 text-white">NEW</AppBadge>
            <h4 class="line-clamp-2" v-text="post.title" />
          </div>
        </template>

        <template #description>
          <p class="line-clamp-2 text-sm" v-text="post.description" />
        </template>

        <template #footer>
          <!-- The link variant paints white under itself; the gradient has to show through. -->
          <AppButton
            as="router-link"
            :to="`/changelog#post-${post.id}`"
            variant="link"
            size="sm"
            class="-ml-2 !bg-transparent"
            trailing-icon="heroicons-outline:arrow-right"
            icon-class="[&>path]:stroke-[2.25px]"
            :aria-label="`Learn more: ${post.title}`"
            @click="trackPost(post)"
          >
            Learn more
          </AppButton>
        </template>
      </AppCard>
    </AppCarousel>
  </section>
</template>
